import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { PageableTableComponent } from '../../../shared/controls/pageable-table/pageable-table.component';
import { AutoCompleteDropdownComponent } from '../../../shared/controls/auto-complete-dropdown/auto-complete-dropdown.component';

import { DialogService } from '../../../shared/controls/message-dialog/message-dialog.service';
import { ToastService } from '../../../shared/toast/toast.service';
import { ErrorService } from '../../../shared/services/error-service';
import { InitializerService } from '../../../shared/services/initializer-service';
import { AuthService } from '../../../shared/services/auth.service';
import { LoadingService } from '../../../shared/services/loading.service';
import { LoadingHelper } from '../../../shared/services/loading-helper';

import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { AreaConstants } from '../../../shared/constants/area.constants';
import { PermissionConstants } from '../../../shared/constants/permission.constants';

import { MatchingService, PeriodService, MatchingBatchesItem, PagingData, AllPeriodItem } from '../../../../service/proxy';

@Component({
    templateUrl: './matching-list.component.html',
    styleUrls: ['./matching-list.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
        PageableTableComponent,
        AutoCompleteDropdownComponent,
        HasPermissionDirective,
    ],

    providers: [
        MatchingService,
        PeriodService
    ]
})
export class MatchingComponent implements OnInit {
    public initialized = false;
    public isSaving: boolean = false;

    public batchId: number = 0;
    public matchingBatch: any = {};
    public selectedStatus: boolean = false;
    public selectedPeriodId: number | undefined = undefined;

    public search: string = '';
    public paging: PagingData = new PagingData();
    public periods: AllPeriodItem[] = [];
    public batches: MatchingBatchesItem[] = [];

    public statusOptions = [
        { id: 0, label: 'Pending' },
        { id: 1, label: 'Completed' }
    ];

    public showEdit: boolean = false;
    public showDelete: boolean = false;

    constructor(
        public authService: AuthService,
        private router: Router,
        private matchingService: MatchingService,
        private initializerService: InitializerService,
        private periodService: PeriodService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private dialogService: DialogService,
        private loadingService: LoadingService)
    {
        this.paging = this.initializerService.createPagingData();
    }

    ngOnInit(): void {
        LoadingHelper.withLoading(
            this.periodService.getAllPeriods(),
            this.loadingService
        ).subscribe((data) => {
            this.initialized = true;
            this.periods = data.results || [];
            this.selectedPeriodId = this.periods[1]?.id ?? 0;

            this.refreshGridData();
            this.initializePermissions();
        });
    }

    initializePermissions(): void {
        this.showEdit =
            this.authService.hasPermission(AreaConstants.Matching, PermissionConstants.Matching);

        this.showDelete =
            (this.selectedStatus === false && this.authService.hasPermission(AreaConstants.Matching, PermissionConstants.DeletePending)) ||
            (this.selectedStatus === true && this.authService.hasPermission(AreaConstants.Matching, PermissionConstants.DeleteCompleted));
    }

    formatPeriod = (period: AllPeriodItem): string =>
        `${period?.year}-${String(period?.month).padStart(2, '0')}`;

    displayStatus = (status: any): string => status?.label ?? '';

    onStatusSelectionChange(statusId: number | null): void {
        this.selectedStatus = statusId === 1;
        this.refreshGridData();
        this.initializePermissions();
    }

    onPeriodSelectionChange(periodId: number | null): void {
        this.selectedPeriodId = periodId ?? 0;
        this.getMatchingBatches(this.paging);
    }

    getMatchingBatch(batchId: number): void {
        LoadingHelper.withLoading(
            this.matchingService.getMatchingBatch(batchId),
            this.loadingService
        ).subscribe((result) => {
            this.matchingBatch = result;
        });
    }

    getColumnMap() {
        return {
            createdAt: 'Date Created',
            period: 'Period',
            clientName: 'Client',
            areaName: 'Policy',
        };
    }

    refreshGridData(): void {
        this.paging = this.initializerService.createPagingData();
        this.paging.sortColumn = 'createdAt';
        this.paging.sortDirection = 'Desc'; //latest first
        this.getMatchingBatches(this.paging);
    }

    getMatchingBatches(pagingData: PagingData): void {
        if (!this.initialized) {
            return;
        }

        const search = `${this.selectedPeriodId}|${this.search}`;

        LoadingHelper.withLoading(
            this.matchingService.getMatchingBatches(this.selectedStatus, search, pagingData.pageNo, pagingData.pageSize, pagingData.sortDirection, pagingData.sortColumn),
            this.loadingService
        ).subscribe({
            next: (data) => {
                this.paging = data.pagingData;
                this.batches = data.results || [];
            },
            error: (error) => {
                if (error.status === 204) {
                    this.batches = [];
                }
                else {
                    this.errorService.handleError(error);
                }
            }
        });
    }

    createMatchingBatch(): void {
        this.router.navigate([`/matching-create`]);
    }

    editMatchingBatch(batchId: number): void {
        //this.router.navigate([`/matching-wizard/${batchId}`]);
        this.router.navigate([`/matching-wizard/${batchId}`], { queryParams: { tab: 'records' } });
    }

    deleteMatchingBatch(batchId: number): void {
        const title = this.selectedStatus
            ? 'Delete COMPLETED Batch'
            : 'Delete Pending Batch';

        const message = this.selectedStatus
            ? 'You are about to delete a COMPLETED batch. Are you sure?'
            : 'Are you sure you want to delete this batch?';

        const toastMessage = this.selectedStatus
            ? 'Completed batch deleted successfully.'
            : 'Pending batch deleted successfully.';

        if (!this.selectedStatus) {
            this.deletePendingMatchingBatch(batchId, title, message, toastMessage);
        }
        else {
            this.deleteCompletedMatchingBatch(batchId, title, message, toastMessage);
        };
    }

    deletePendingMatchingBatch(batchId: number, title: string, message: string, toastMessage: string): void {
        this.dialogService.show(title, message, 'Warning')
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    LoadingHelper.withLoading(
                        this.matchingService.deletePendingMatchingBatch(batchId),
                        this.loadingService
                    ).subscribe({
                        next: () => {
                            this.getMatchingBatches(this.paging);
                        },
                        error: (error: any) => {
                            this.errorService.handleError(error);
                        },
                        complete: () => {
                            this.toastService.show(toastMessage, 'success');
                        }
                    });
                }
            }
        );
    }

    deleteCompletedMatchingBatch(batchId: number, title: string, message: string, toastMessage: string): void {
        this.dialogService.show(title, message, 'Warning')
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    LoadingHelper.withLoading(
                        this.matchingService.deleteCompletedMatchingBatch(batchId),
                        this.loadingService
                    ).subscribe({
                        next: () => {
                            this.getMatchingBatches(this.paging);
                        },
                        error: (error: any) => {
                            this.errorService.handleError(error);
                        },
                        complete: () => {
                            this.toastService.show(toastMessage, 'success');
                        }
                    });
                }
            }
        );
    }
}
